import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApiError } from 'src/api/axiosClient/types';
import { useGetContactCustomFieldsApi } from 'src/api/customFields';
import { ContactDefaultCustomField } from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { debounce } from 'ts-debounce';
import { useGroupsApi } from '../api';
import { buildFormData, buildTestRulesRequestData, buildUpdateRequestData } from '../helpers';
import { useGroupService } from '../services';
import { Group, GroupFormData } from '../types';

export interface DynamicGroupRulesFormDialogController {
  isOpen: boolean;
  initFormData: GroupFormData | null;
  customFields: ContactDefaultCustomField[] | null;
  customGroups: Group[];
  isContactsCountInfoLoading: boolean;
  contactsCountInfo: {
    status: 'success' | 'error';
    validContactsCount?: number;
    invalidContactsCount?: number;
  } | null;
  error: ApiError | null;
  open: (group: Group) => Promise<boolean>;
  close: () => void;
  testRules: (rules: GroupFormData['rules'] | null) => void;
  submit: (
    full: GroupFormData,
    dirty: Partial<GroupFormData>,
    nextAction?: (group: Group) => unknown | Promise<unknown>,
  ) => Promise<Group | undefined>;
}

export const useDynamicGroupRulesFormDialogController =
  (): DynamicGroupRulesFormDialogController => {
    const currentCompanyId = useSelector(getCurrentCompanyId) as string;
    const [initFormData, setInitFormData] = useState<GroupFormData | null>(null);
    const [customFields, setCustomFields] = useState<ContactDefaultCustomField[] | null>(null);
    const [customGroups, setCustomGroups] = useState<Group[]>([]);
    const [contactsCountInfo, setContactsCountInfo] = useState<{
      status: 'success' | 'error';
      validContactsCount?: number;
      invalidContactsCount?: number;
    } | null>(null);
    const activeGroupRef = useRef<Group | null>(null);

    const getCustomFields = useGetContactCustomFieldsApi();
    const groupsApi = useGroupsApi();
    const { loading, getGroup, updateGroup, testGroupRules, error } = useGroupService();

    const open = useCallback(
      async (group: Group) => {
        activeGroupRef.current = group;
        setContactsCountInfo(null);

        try {
          const [customFieldsResponse, groupsResponse] = await Promise.all([
            getCustomFields({
              companyId: currentCompanyId!,
              sort: 'description_asc',
            }),
            groupsApi.getGroups({
              companyId: currentCompanyId!,
              offset: 0,
              limit: 1000,
              types: 'custom',
              statuses: 'active',
            }),
          ]);

          setCustomFields(customFieldsResponse.data.items);
          setCustomGroups(
            groupsResponse.data.items.filter(
              g => g.type === 'custom' && g.status === 'active' && g.id !== group.id,
            ),
          );
          setInitFormData(buildFormData(group));
          return true;
        } catch (e) {
          console.log(e);
          activeGroupRef.current = null;
          return false;
        }
      },
      [getCustomFields, groupsApi, currentCompanyId],
    );

    const close = useCallback(() => {
      setInitFormData(null);
      setCustomGroups([]);
      setContactsCountInfo(null);
    }, []);

    const testRules = useCallback(async (rules: GroupFormData['rules'] | null) => {
      if (rules?.length) {
        try {
          const response = await testGroupRules(currentCompanyId, buildTestRulesRequestData(rules));
          setContactsCountInfo({
            status: 'success',
            validContactsCount: response.data.validContactsCount,
            invalidContactsCount: response.data.invalidContactsCount,
          });
        } catch (e) {
          setContactsCountInfo({
            status: 'error',
          });
        }
      } else {
        setContactsCountInfo(null);
      }
    }, []);

    const testRulesDebounce = useMemo(() => debounce(testRules, 500), []);

    const testRulesConditional = useCallback(
      (rules: GroupFormData['rules'] | null) => {
        if (rules?.length) {
          testRulesDebounce(rules);
        } else {
          testRules(rules);
        }
      },
      [testRules, testRulesDebounce],
    );

    const submit = useCallback(
      async (
        full: GroupFormData,
        dirty: Partial<GroupFormData>,
        nextAction?: (group: Group) => unknown | Promise<unknown>,
      ) => {
        const { id: groupId } = full;
        const sourceGroup = activeGroupRef.current;

        try {
          await updateGroup(currentCompanyId, groupId, buildUpdateRequestData(full));
        } catch (e) {
          console.log(e);
          return;
        }

        close();
        let savedGroup: Group | undefined;

        try {
          savedGroup = await getGroup(currentCompanyId, groupId);
        } catch (e) {
          console.log(e);

          if (sourceGroup) {
            savedGroup = {
              ...sourceGroup,
              ...full,
              validContactCount:
                contactsCountInfo?.validContactsCount ?? sourceGroup.validContactCount,
              invalidContactCount:
                contactsCountInfo?.invalidContactsCount ?? sourceGroup.invalidContactCount,
            };
          }
        }

        if (savedGroup) {
          activeGroupRef.current = savedGroup;
          try {
            await nextAction?.(savedGroup);
          } catch (e) {
            console.log(e);
          }
        }

        return savedGroup;
      },
      [close, contactsCountInfo, currentCompanyId, getGroup, updateGroup],
    );

    return {
      isOpen: !!initFormData,
      initFormData,
      customFields,
      customGroups,
      isContactsCountInfoLoading: loading.testGroupRules,
      contactsCountInfo,
      error,
      open,
      close,
      testRules: testRulesConditional,
      submit,
    };
  };
